type AnswersMap = Record<string, string | undefined>;

type SurveyFieldConfig = {
  fieldKey: string;
  questionI18nKey: string;
  isHidden?: (answers: AnswersMap) => boolean;
  isFreeText?: boolean;
  answerI18nGroup?: string;
  sharedAnswerI18nGroups?: string[];
};

export type SurveyTypeConfig = {
  fields: SurveyFieldConfig[];
  answerI18nKeys: Record<string, Record<string, string>>;
};

const hideOtherFollowUp =
  (parentFieldKey: string, otherValue = 'other') =>
  (answers: AnswersMap) =>
    answers[parentFieldKey] !== otherValue;

const hideParentWhenOtherSelected =
  (fieldKey: string, otherValue = 'other') =>
  (answers: AnswersMap) =>
    answers[fieldKey] === otherValue;

const ONBOARDING_Q1_ANSWERS: Record<string, string> = {
  'skill-new': 'survey_item.onboarding.answers.q1.skill_new',
  'skill-basics': 'survey_item.onboarding.answers.q1.skill_basics',
  'skill-work': 'survey_item.onboarding.answers.q1.skill_work',
  'skill-dev': 'survey_item.onboarding.answers.q1.skill_dev',
  'skill-other': 'survey_item.onboarding.answers.q1.skill_other',
};

const ONBOARDING_Q2_ANSWERS: Record<string, string> = {
  'goal-scratch': 'survey_item.onboarding.answers.q2.goal_scratch',
  'goal-real-ui': 'survey_item.onboarding.answers.q2.goal_real_ui',
  'goal-practice': 'survey_item.onboarding.answers.q2.goal_practice',
  'goal-pro': 'survey_item.onboarding.answers.q2.goal_pro',
  'goal-mockups': 'survey_item.onboarding.answers.q2.goal_mockups',
  'goal-designer': 'survey_item.onboarding.answers.q2.goal_designer',
  'goal-explore': 'survey_item.onboarding.answers.q2.goal_explore',
};

const ONBOARDING_Q3_ANSWERS: Record<string, string> = {
  'src-instagram': 'survey_item.onboarding.answers.q3.src_instagram',
  'src-figma': 'survey_item.onboarding.answers.q3.src_figma',
  'src-tiktok': 'survey_item.onboarding.answers.q3.src_tiktok',
  'src-linkedin': 'survey_item.onboarding.answers.q3.src_linkedin',
  'src-search': 'survey_item.onboarding.answers.q3.src_search',
  'src-other': 'survey_item.onboarding.answers.q3.src_other',
};

const ONBOARDING_LEVEL_ANSWERS: Record<string, string> = {
  novice: 'survey_item.onboarding.answers.level.novice',
  junior: 'survey_item.onboarding.answers.level.junior',
  designer: 'survey_item.onboarding.answers.level.designer',
  developer: 'survey_item.onboarding.answers.level.developer',
  other: 'survey_item.onboarding.answers.level.other',
};

const ONBOARDING_GOAL_ANSWERS: Record<string, string> = {
  'learn-from-scratch': 'survey_item.onboarding.answers.goal.learn_from_scratch',
  'learn-how': 'survey_item.onboarding.answers.goal.learn_how',
  'boost-skills': 'survey_item.onboarding.answers.goal.boost_skills',
  'learn-things': 'survey_item.onboarding.answers.goal.learn_things',
  'build-projects': 'survey_item.onboarding.answers.goal.build_projects',
  'become-designer': 'survey_item.onboarding.answers.goal.become_designer',
  other: 'survey_item.onboarding.answers.goal.other',
};

const ONBOARDING_WHERE_FIND_ANSWERS: Record<string, string> = {
  instagram: 'survey_item.onboarding.answers.where_find.instagram',
  'figma-community': 'survey_item.onboarding.answers.where_find.figma_community',
  tiktok: 'survey_item.onboarding.answers.where_find.tiktok',
  linkedin: 'survey_item.onboarding.answers.where_find.linkedin',
  search: 'survey_item.onboarding.answers.where_find.search',
  other: 'survey_item.onboarding.answers.where_find.other',
};

export const SURVEY_TYPE_CONFIGS: Record<string, SurveyTypeConfig> = {
  ONBOARDING: {
    fields: [
      {
        fieldKey: 'q1',
        questionI18nKey: 'survey_item.onboarding.questions.q1',
        isHidden: hideParentWhenOtherSelected('q1', 'skill-other'),
        answerI18nGroup: 'q1',
      },
      {
        fieldKey: 'q1_other',
        questionI18nKey: 'survey_item.onboarding.questions.q1',
        isHidden: hideOtherFollowUp('q1', 'skill-other'),
        isFreeText: true,
      },
      {
        fieldKey: 'q2',
        questionI18nKey: 'survey_item.onboarding.questions.q2',
        answerI18nGroup: 'q2',
      },
      {
        fieldKey: 'q3',
        questionI18nKey: 'survey_item.onboarding.questions.q3',
        isHidden: hideParentWhenOtherSelected('q3', 'src-other'),
        answerI18nGroup: 'q3',
      },
      {
        fieldKey: 'q3_other',
        questionI18nKey: 'survey_item.onboarding.questions.q3',
        isHidden: hideOtherFollowUp('q3', 'src-other'),
        isFreeText: true,
      },
      {
        fieldKey: 'level',
        questionI18nKey: 'survey_item.onboarding.questions.level',
        isHidden: hideParentWhenOtherSelected('level'),
        answerI18nGroup: 'level',
        sharedAnswerI18nGroups: ['level'],
      },
      {
        fieldKey: 'levelOther',
        questionI18nKey: 'survey_item.onboarding.questions.level',
        isHidden: hideOtherFollowUp('level'),
        isFreeText: true,
      },
      {
        fieldKey: 'role',
        questionI18nKey: 'survey_item.onboarding.questions.level',
        answerI18nGroup: 'level',
        sharedAnswerI18nGroups: ['level'],
      },
      {
        fieldKey: 'experience',
        questionI18nKey: 'survey_item.onboarding.questions.level',
        answerI18nGroup: 'level',
        sharedAnswerI18nGroups: ['level'],
      },
      {
        fieldKey: 'goal',
        questionI18nKey: 'survey_item.onboarding.questions.goal',
        answerI18nGroup: 'goal',
      },
      {
        fieldKey: 'whereFind',
        questionI18nKey: 'survey_item.onboarding.questions.where_find',
        isHidden: hideParentWhenOtherSelected('whereFind'),
        answerI18nGroup: 'where_find',
      },
      {
        fieldKey: 'whereFindOther',
        questionI18nKey: 'survey_item.onboarding.questions.where_find',
        isHidden: hideOtherFollowUp('whereFind'),
        isFreeText: true,
      },
    ],
    answerI18nKeys: {
      q1: ONBOARDING_Q1_ANSWERS,
      q2: ONBOARDING_Q2_ANSWERS,
      q3: ONBOARDING_Q3_ANSWERS,
      level: ONBOARDING_LEVEL_ANSWERS,
      goal: ONBOARDING_GOAL_ANSWERS,
      where_find: ONBOARDING_WHERE_FIND_ANSWERS,
    },
  },
  NEW_USERS_SURVEY: {
    fields: [
      {
        fieldKey: 'characterize',
        questionI18nKey: 'survey_item.new_users_survey.questions.characterize',
        isHidden: hideParentWhenOtherSelected('characterize', 'Other'),
        answerI18nGroup: 'characterize',
      },
      {
        fieldKey: 'characterizeOther',
        questionI18nKey: 'survey_item.new_users_survey.questions.characterize',
        isHidden: hideOtherFollowUp('characterize', 'Other'),
        isFreeText: true,
      },
      {
        fieldKey: 'aspects',
        questionI18nKey: 'survey_item.new_users_survey.questions.aspects',
        isHidden: hideParentWhenOtherSelected('aspects', 'Other'),
        answerI18nGroup: 'aspects',
      },
      {
        fieldKey: 'aspectsOther',
        questionI18nKey: 'survey_item.new_users_survey.questions.aspects',
        isHidden: hideOtherFollowUp('aspects', 'Other'),
        isFreeText: true,
      },
      {
        fieldKey: 'expectations',
        questionI18nKey: 'survey_item.new_users_survey.questions.expectations',
        answerI18nGroup: 'expectations',
      },
      {
        fieldKey: 'howFind',
        questionI18nKey: 'survey_item.new_users_survey.questions.how_find',
        isHidden: hideParentWhenOtherSelected('howFind', 'Other'),
        answerI18nGroup: 'how_find',
      },
      {
        fieldKey: 'howFindOther',
        questionI18nKey: 'survey_item.new_users_survey.questions.how_find',
        isHidden: hideOtherFollowUp('howFind', 'Other'),
        isFreeText: true,
      },
    ],
    answerI18nKeys: {
      characterize: {
        ux_newbie: 'survey_item.new_users_survey.answers.characterize.ux_newbie',
        ux_pro: 'survey_item.new_users_survey.answers.characterize.ux_pro',
        graphic: 'survey_item.new_users_survey.answers.characterize.graphic',
        owner: 'survey_item.new_users_survey.answers.characterize.owner',
        developer: 'survey_item.new_users_survey.answers.characterize.developer',
        Other: 'survey_item.new_users_survey.answers.other',
      },
      aspects: {
        prototyping: 'survey_item.new_users_survey.answers.aspects.prototyping',
        mastering: 'survey_item.new_users_survey.answers.aspects.mastering',
        vector: 'survey_item.new_users_survey.answers.aspects.vector',
        responsive_design:
          'survey_item.new_users_survey.answers.aspects.responsive_design',
        Other: 'survey_item.new_users_survey.answers.other',
      },
      expectations: {
        theoretical_knowledge:
          'survey_item.new_users_survey.answers.expectations.theoretical_knowledge',
        hands_on: 'survey_item.new_users_survey.answers.expectations.hands_on',
        portfolio: 'survey_item.new_users_survey.answers.expectations.portfolio',
        discuss: 'survey_item.new_users_survey.answers.expectations.discuss',
        explore: 'survey_item.new_users_survey.answers.expectations.explore',
      },
      how_find: {
        instagram: 'survey_item.new_users_survey.answers.how_find.instagram',
        figma: 'survey_item.new_users_survey.answers.how_find.figma',
        tiktok: 'survey_item.new_users_survey.answers.how_find.tiktok',
        search: 'survey_item.new_users_survey.answers.how_find.search',
        linkedin: 'survey_item.new_users_survey.answers.how_find.linkedin',
        Other: 'survey_item.new_users_survey.answers.other',
      },
    },
  },
  TRIAL_COURSE: {
    fields: [
      {
        fieldKey: 'mostUseful',
        questionI18nKey: 'survey_item.trial_course.questions.most_useful',
        answerI18nGroup: 'most_useful',
      },
      {
        fieldKey: 'learningBlocker',
        questionI18nKey: 'survey_item.trial_course.questions.learning_blocker',
        answerI18nGroup: 'learning_blocker',
      },
      {
        fieldKey: 'mainGoal',
        questionI18nKey: 'survey_item.trial_course.questions.main_goal',
        answerI18nGroup: 'main_goal',
      },
    ],
    answerI18nKeys: {
      most_useful: {
        'specific-topic-practice':
          'survey_item.trial_course.answers.most_useful.specific_topic_practice',
        'figma-simulator-overview':
          'survey_item.trial_course.answers.most_useful.figma_simulator_overview',
        'reinforced-theory':
          'survey_item.trial_course.answers.most_useful.reinforced_theory',
        'figma-easier-than-expected':
          'survey_item.trial_course.answers.most_useful.figma_easier_than_expected',
        'nothing-memorable':
          'survey_item.trial_course.answers.most_useful.nothing_memorable',
      },
      learning_blocker: {
        'subscription-cost-high':
          'survey_item.trial_course.answers.learning_blocker.subscription_cost_high',
        'learning-format-not-suitable':
          'survey_item.trial_course.answers.learning_blocker.learning_format_not_suitable',
        'other-tools-not-only-figma':
          'survey_item.trial_course.answers.learning_blocker.other_tools_not_only_figma',
        'no-time': 'survey_item.trial_course.answers.learning_blocker.no_time',
        'knowledge-enough':
          'survey_item.trial_course.answers.learning_blocker.knowledge_enough',
        'show-pricing':
          'survey_item.trial_course.answers.learning_blocker.show_pricing',
      },
      main_goal: {
        'starting-in-design':
          'survey_item.trial_course.answers.main_goal.starting_in_design',
        'designer-leveling-up':
          'survey_item.trial_course.answers.main_goal.designer_leveling_up',
        'related-specialist':
          'survey_item.trial_course.answers.main_goal.related_specialist',
        'own-product-startup':
          'survey_item.trial_course.answers.main_goal.own_product_startup',
        'course-university':
          'survey_item.trial_course.answers.main_goal.course_university',
      },
    },
  },
};
